import { useEffect, useState } from 'react';
import Select, { type MultiValue, type SingleValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'context/SettingsContext';
import { useTheme } from 'context/ThemeContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import InputLabel from 'components/others/InputLabel';
import { isTeamActive } from 'utils/helpers/teams/isTeamActive';

export interface TeamOption {
    value: number;
    label: string;
}

type TeamSelectBaseProps = {
    isClearable?: boolean;
    showEmpty?: boolean;
    disabled?: boolean;
    hideLabel?: boolean;
    placeholder?: string;
    className?: string;
};

export type TeamSelectProps =
    | (TeamSelectBaseProps & {
          isMulti?: false;
          value?: number | null;
          onChange: (opt: SingleValue<TeamOption>) => void;
      })
    | (TeamSelectBaseProps & {
          isMulti: true;
          value?: number[];
          onChange: (opt: MultiValue<TeamOption>) => void;
      });

const TeamSelect = ({
    value,
    isMulti = false,
    isClearable = false,
    showEmpty = false,
    onChange,
    disabled = false,
    hideLabel = false,
    placeholder,
    className = 'w-100',
}: TeamSelectProps) => {
    const { t } = useTranslation();
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const [selectTheme, setSelectTheme] = useState(darkTheme ? selectStylesDark : selectStylesLigth);
    const [options, setOptions] = useState<TeamOption[]>([]);

    useEffect(() => {
        setOptions(
            localSettings?.DIRS?.Team
                ?.filter((team) => (showEmpty || team.ID) && isTeamActive(team))
                ?.map((team) => ({ value: team.ID, label: team.teamName })) || [],
        );
    }, [localSettings?.DIRS?.Team, showEmpty]);

    useEffect(() => {
        setSelectTheme(darkTheme ? selectStylesDark : selectStylesLigth);
    }, [darkTheme]);

    const selectedValue = isMulti
        ? options.filter((opt) => Array.isArray(value) && value.includes(opt.value))
        : options.find((opt) => opt.value == value) || null;

    const handleChange = (opt: SingleValue<TeamOption> | MultiValue<TeamOption>) => {
        if (isMulti) {
            (onChange as (next: MultiValue<TeamOption>) => void)(opt as MultiValue<TeamOption>);
            return;
        }
        (onChange as (next: SingleValue<TeamOption>) => void)(opt as SingleValue<TeamOption>);
    };

    return (
        <InputLabel title={hideLabel ? '' : t('team')}>
            <Select<TeamOption, boolean>
                options={options}
                isDisabled={disabled}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={{
                    ...selectTheme,
                    control: (base, state) => ({
                        ...base,
                        ...(selectTheme.control ? selectTheme.control(base, state) : {}),
                    }),
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 2000,
                    }),
                }}
                className={className}
                isClearable={isClearable}
                isMulti={isMulti}
                placeholder={placeholder}
                value={selectedValue}
                onChange={handleChange}
            />
        </InputLabel>
    );
};

export default TeamSelect;
